import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppAuth } from '../context/AuthContext';
import './EventManagementPage.css';

export default function XboxCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isLoaded, isSignedIn, getToken } = useAppAuth();
  const [error, setError] = useState(null);
  const exchanged = useRef(false);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || exchanged.current) return;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const oauthError = searchParams.get('error');
    const errorDesc = searchParams.get('error_description');

    if (oauthError) {
      sessionStorage.removeItem('xbox_oauth_state');
      sessionStorage.removeItem('xbox_redirect_pending');
      setError(errorDesc || oauthError);
      return;
    }

    const savedState = sessionStorage.getItem('xbox_oauth_state');
    if (!state || state !== savedState) {
      setError('Invalid OAuth state. Please try again.');
      sessionStorage.removeItem('xbox_oauth_state');
      sessionStorage.removeItem('xbox_redirect_pending');
      return;
    }

    exchanged.current = true;
    sessionStorage.removeItem('xbox_oauth_state');

    const redirectUri = `${window.location.origin}/xbox-callback`;

    (async () => {
      try {
        const token = await getToken();
        const res = await fetch('/api/admin/events?action=xbox-exchange', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ code, redirectUri }),
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Exchange failed');

        // Store linked profile so pages can display it immediately
        sessionStorage.setItem('mc_linked_profile', JSON.stringify({
          uuid: data.minecraftUuid,
          username: data.minecraftUsername,
        }));
        // Return to the page that initiated the link (event page or admin)
        const returnPath = sessionStorage.getItem('xbox_return_path') || '/panel';
        sessionStorage.removeItem('xbox_return_path');
        navigate(returnPath, { replace: true });
      } catch (err) {
        sessionStorage.removeItem('xbox_redirect_pending');
        setError(err.message);
      }
    })();
  }, [isLoaded, isSignedIn, searchParams, getToken, navigate]);

  if (error) {
    return (
      <section className="em-page">
        <div className="em-container">
          <div className="em-header">
            <h1 className="em-title">Linking Failed</h1>
          </div>
          <div className="em-panel" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#cc3333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '1.5rem' }}>
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            <p style={{ color: '#b5b8c0', fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '2rem', fontFamily: "'Nunito', sans-serif" }}>
              {error}
            </p>
            <button
              className="em-btn-save"
              onClick={() => {
                const returnPath = sessionStorage.getItem('xbox_return_path') || '/panel';
                sessionStorage.removeItem('xbox_return_path');
                navigate(returnPath, { replace: true });
              }}
            >
              Continue
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="em-page">
      <div className="em-container">
        <div className="em-header">
          <h1 className="em-title">Linking Minecraft</h1>
        </div>
        <div className="em-panel" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
          <div className="em-spinner" style={{ margin: '0 auto 1.5rem' }} />
          <p style={{ color: '#b5b8c0', fontSize: '1.1rem', fontFamily: "'Nunito', sans-serif", margin: 0 }}>
            Connecting your Minecraft account...
          </p>
        </div>
      </div>
    </section>
  );
}
